import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import type { GenericArray } from '@Types';
// NOTE: this needs to be directed imported
import useADialog from '@/components/atoms/ADialog/useADialog';
import { TOAST_TYPES } from '@/components/atoms/AToast/types';
import { useSessionStore } from '@Stores';
import { getApplicationStatusChangeModalProps } from '@PeopleModule';

// The complement of api's ApplicationLockHelper::ACTIVE_STATUSES (submitted / pending / approved).
// Keep the two lists in step - api rejects the write these options are meant to prevent.
const INACTIVE_APPLICATION_STATUSES = ['canceled', 'declined', 'deleted', 'do not adopt', 'do not foster', 'expired'];

const APPLICATION_LOCKED_STATUS = 409;

const TOAST_APPLICATION_LOCKED = {
  message: 'adoption.oneActiveApplicationHint',
  title: 'adoption.title.cannotReactivate',
  type: TOAST_TYPES.ERROR,
};

const TOAST_UNKNOWN_ISSUE = {
  message: 'errors.generics.unknownIssue.message',
  title: 'errors.generics.unknownIssue.title',
  type: TOAST_TYPES.ERROR,
};

/**
 * The error toast for a failed application status change. Pass the rejected error straight in.
 *
 * api answers 409 when a dead adoption application would come back to life while the person still
 * holds an active one (ApplicationLockHelper). That is a rule staff can act on, not a failure, so it
 * gets the real explanation — the same sentence the locked dropdown's tooltip already shows.
 * Anything else stays generic, because we cannot know what it was.
 *
 * **Never pass api's own message string to notify().** AToast renders both title and message through
 * `$t()`, so a raw English sentence shows untranslated and logs a missing-key warning every time.
 */
function getStatusChangeErrorToast(error: any) {
  const IS_APPLICATION_LOCKED = error?.response?.status === APPLICATION_LOCKED_STATUS;

  return IS_APPLICATION_LOCKED ? TOAST_APPLICATION_LOCKED : TOAST_UNKNOWN_ISSUE;
}

/**
 * A locked application is one that is NOT active while the person holds another active one. It stays
 * editable — it just may not come back to life, so only the inactive statuses are offered. An active
 * application is never locked and keeps every option.
 */
function filterLockedOptions<T extends { value: string }>(options: T[], isLocked: boolean) {
  if (!isLocked) return options;

  return options.filter((option) => INACTIVE_APPLICATION_STATUSES.includes(option.value.toLowerCase()));
}

export default function () {
  const { shelterProfile } = storeToRefs(useSessionStore());

  const { activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();
  const { t } = useI18n();

  function onOpenAdoptApplicationStatusModal(application: any, adopterStatusModel?: any, isMultiple?: boolean) {
    if (!adopterStatusModel) return;

    const contentTarget: GenericArray = {
      canceled: t('adoption.modal.application.canceled'),
      declined: t('adoption.modal.application.declined'),
      expired: t('adoption.modal.application.expired'),
      submitted: t('adoption.modal.application.submitted'),
      approved: t('adoption.modal.application.approved'),
      pending: t('adoption.modal.application.pending'),
      'do not adopt': t('adoption.modal.application.doNotAdopt'),
    };

    const { adopt_user_id, application_id, _id, people_id, full_name, status, adopter_status, people } = application;
    const currentStatus = status ?? adopter_status;

    let content: string;
    let secondContent = '';
    let confirmWordKey: string | undefined;
    let isDeclinedApplication = false;

    if (!isMultiple) {
      const modalProps = getApplicationStatusChangeModalProps({
        oldStatus: currentStatus ?? '',
        newStatus: adopterStatusModel,
        formType: 'adoption',
        adopterStatus: adopter_status ?? application?.adopter_status,
        doNotAdopt: people?.do_not_adopt ?? application?.do_not_adopt,
        contentTarget,
        t,
        shelterProfile: shelterProfile?.value,
      });
      content = modalProps.content;
      secondContent = modalProps.secondContent;
      confirmWordKey = modalProps.confirmWordKey;
      isDeclinedApplication = modalProps.isDeclinedApplication;
    } else {
      const isDeclinedEmailEnabled = shelterProfile?.value?.application_declined_email_status !== false;
      const isDeclinedOrDoNotAdopt = adopterStatusModel === 'declined' || adopterStatusModel === 'do not adopt';
      const shouldAnnounceEmail =
        (adopterStatusModel === 'approved' && shelterProfile?.value?.application_approved_email_status !== false) ||
        (adopterStatusModel === 'canceled' && shelterProfile?.value?.application_canceled_email_status !== false) ||
        (adopterStatusModel === 'declined' && isDeclinedEmailEnabled);

      secondContent = shouldAnnounceEmail
        ? t('adoption.modal.application.emailSent', { status: adopterStatusModel })
        : '';
      content = t('adoption.modal.areYouSure', { action: contentTarget[adopterStatusModel] });
      isDeclinedApplication = isDeclinedOrDoNotAdopt && isDeclinedEmailEnabled;
    }

    let payload: any = {
      adopt_user_id: adopt_user_id?.$oid ?? adopt_user_id ?? people?.adopt_user_id?.$oid ?? people?.adopt_user_id,
      application_id: application_id?.$oid ?? application_id,
      form_type: 'adoption',
      old_status: currentStatus,
      people_id: people_id ?? _id?.$oid ?? _id,
      adopter_name: full_name,
      status: adopterStatusModel,
      shelter_id: shelterProfile.value?._id?.$oid,
    };

    if (isMultiple) {
      const selectedItems = application.map((selectedApplication: any) => ({
        adopt_user_id:
          selectedApplication?.adopt_user_id?.$oid ??
          selectedApplication?.adopt_user_id ??
          selectedApplication?.people?.adopt_user_id?.$oid ??
          selectedApplication?.people?.adopt_user_id,
        application_id: selectedApplication?.application_id?.$oid ?? selectedApplication?.application_id,
        form_type: selectedApplication.form_type,
        people_id: selectedApplication.people_id?.$oid || selectedApplication.people_id,
        shelter_id: selectedApplication.shelter_id?.$oid,
      }));

      payload = {
        applications: selectedItems,
        old_status: '',
        status: adopterStatusModel,
      };
    }

    const personData =
      !isMultiple && adopterStatusModel === 'do not adopt' ? { groups: people?.groups ?? [] } : undefined;

    activeModalProps.value = {
      clickEvent: payload,
      isMultiple,
      secondContent,
      content,
      personData,
      ...(confirmWordKey && { confirmWordKey }),
    };

    activeModal.value = isDeclinedApplication ? SYSTEM_MODALS.APPLICATION_DECLINED : SYSTEM_MODALS.BASIC_MODAL;
    dialogVisibility.value = true;
  }

  function onOpenFosterApplicationStatusModal(application: any, statusModel: string) {
    if (!statusModel) return;

    const contentTarget: GenericArray = {
      canceled: t('adoption.modal.application.canceled'),
      declined: t('adoption.modal.application.declined'),
      expired: t('adoption.modal.application.expired'),
      submitted: t('adoption.modal.application.submitted'),
      approved: t('adoption.modal.application.approved'),
      pending: t('adoption.modal.application.pending'),
      deleted: t('filters.options.deleted'),
      'do not foster': t('adoption.modal.application.doNotFoster'),
    };

    const { _id, status, form_type, people_id, people } = application;

    const modalProps = getApplicationStatusChangeModalProps({
      oldStatus: status ?? '',
      newStatus: statusModel,
      formType: form_type,
      doNotFoster: people?.do_not_foster ?? application?.do_not_foster,
      contentTarget,
      t,
      shelterProfile: shelterProfile?.value,
    });

    const personData = statusModel === 'do not foster' ? { groups: people?.groups ?? [] } : undefined;

    const payload = {
      application_id: _id?.$oid,
      form_type,
      old_status: status,
      people_id: people_id?.$oid || people_id,
      status: statusModel,
    };

    activeModalProps.value = {
      clickEvent: payload,
      isMultiple: false,
      content: modalProps.content,
      secondContent: modalProps.secondContent,
      personData,
      ...(modalProps.confirmWordKey && { confirmWordKey: modalProps.confirmWordKey }),
    };

    activeModal.value = SYSTEM_MODALS.BASIC_MODAL;
    dialogVisibility.value = true;
  }

  function onOpenOtherApplicationStatusModal(
    application?: any,
    statusModel?: any,
    isMultiple?: boolean,
    formType?: string
  ) {
    if (!statusModel) return;

    const contentTarget: GenericArray = {
      canceled: t('adoption.modal.application.canceled'),
      declined: t('adoption.modal.application.declined'),
      expired: t('adoption.modal.application.expired'),
      submitted: t('adoption.modal.application.submitted'),
      approved: t('adoption.modal.application.approved'),
      pending: t('adoption.modal.application.pending'),
      deleted: t('filters.options.deleted'),
      'do not foster': t('adoption.modal.application.doNotFoster'),
    };

    const firstItem = isMultiple ? application?.[0] : application;
    const { adopt_user_id, application_id, _id, status, form_type } = firstItem ?? {};

    let content: string;
    let secondContent = '';
    let confirmWordKey: string | undefined;

    if (formType === 'foster' && statusModel === 'do not foster') {
      const modalProps = getApplicationStatusChangeModalProps({
        oldStatus: status ?? '',
        newStatus: statusModel,
        formType: 'foster',
        doNotFoster: firstItem?.people?.do_not_foster ?? firstItem?.do_not_foster,
        contentTarget,
        t,
        shelterProfile: shelterProfile?.value,
      });
      content = modalProps.content;
      secondContent = modalProps.secondContent;
      confirmWordKey = modalProps.confirmWordKey;
    } else {
      content = t('adoption.modal.areYouSure', {
        action: contentTarget[statusModel] ?? contentTarget[statusModel?.toLowerCase()],
      });
    }

    let payload: any = {
      adopt_user_id: adopt_user_id?.$oid,
      application_id: application_id?.$oid,
      form_type: form_type ?? formType,
      old_status: status,
      people_id: _id?.$oid || _id,
      status: statusModel,
      shelter_id: shelterProfile.value?._id?.$oid,
    };

    if (isMultiple) {
      const selectedItems = application.map((item: any) => ({
        adopt_user_id: item?.adopt_user_id?.$oid,
        application_id: item?.application_id?.$oid ?? item?._id?.$oid,
        form_type: item.form_type ?? formType,
        people_id: item.people_id?.$oid || item.people_id,
        shelter_id: item.shelter_id?.$oid,
        _id: item?._id,
      }));
      payload = {
        applications: selectedItems,
        old_status: '',
        status: statusModel,
      };
    }

    activeModalProps.value = {
      clickEvent: payload,
      isMultiple,
      content,
      secondContent,
      ...(confirmWordKey && { confirmWordKey }),
    };

    activeModal.value = SYSTEM_MODALS.BASIC_MODAL;
    dialogVisibility.value = true;
  }

  function useOptions(isFormsAndApplications: boolean | 'profile' | 'foster' = false) {
    if (isFormsAndApplications === 'profile') {
      return [
        { label: 'filters.options.approved', value: 'approved' },
        { label: 'filters.options.submitted', value: 'submitted' },
        { label: 'filters.options.pending', value: 'pending' },
        { label: 'filters.options.canceled', value: 'canceled' },
        { label: 'filters.options.declined', value: 'declined' },
        { label: 'filters.options.expired', value: 'expired' },
        { label: 'filters.options.doNotAdopt', value: 'do not adopt' },
      ];
    }
    const lastOption =
      isFormsAndApplications === 'foster'
        ? { label: 'filters.options.doNotFoster', value: 'do not foster' }
        : isFormsAndApplications
          ? { label: 'filters.options.deleted', value: 'deleted' }
          : { label: 'filters.options.doNotAdopt', value: 'do not adopt' };
    return [
      { label: 'filters.options.submitted', value: 'submitted' },
      { label: 'filters.options.pending', value: 'pending' },
      { label: 'filters.options.approved', value: 'approved' },
      { label: 'filters.options.canceled', value: 'canceled' },
      { label: 'filters.options.declined', value: 'declined' },
      { label: 'filters.options.expired', value: 'expired' },
      lastOption,
    ];
  }

  return {
    filterLockedOptions,
    getStatusChangeErrorToast,
    INACTIVE_APPLICATION_STATUSES,
    onOpenAdoptApplicationStatusModal,
    onOpenFosterApplicationStatusModal,
    onOpenOtherApplicationStatusModal,
    useOptions,
  };
}
